import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../api/client';
import type { ReferralPayoutDate, SaveReferralPayoutDateRequest } from '../../api/types';
import { useHubContext } from '../../shell/HubContext';
import { EditIcon, RemoveIcon } from './RowIcons';
import { formatDate } from './referralDates';

/** A run being edited, or the blank one that adds a date. */
type Draft = {
  id: string | null;
  payPeriodStart: string;
  payPeriodEnd: string;
  payoutOn: string;
  note: string;
};

const BLANK: Draft = { id: null, payPeriodStart: '', payPeriodEnd: '', payoutOn: '', note: '' };

/**
 * The payroll calendar. A referral's payout date is the first run here on or after the day its
 * probation is cleared — so this page is what those dates are read from, and moving a run moves every
 * referral that resolves to it.
 */
export function ReferralPayoutDatesSection() {
  const { api } = useHubContext();

  const [payoutDates, setPayoutDates] = useState<ReferralPayoutDate[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const load = useCallback(
    async (signal?: AbortSignal) => {
      const { payoutDates: loaded } = await api.listReferralPayoutDates(signal);
      setPayoutDates(loaded);
    },
    [api],
  );

  useEffect(() => {
    const controller = new AbortController();

    load(controller.signal).catch((error: unknown) => {
      if (controller.signal.aborted) return;

      setLoadError(error instanceof Error ? error.message : 'The payout dates could not be loaded.');
    });

    return () => controller.abort();
  }, [load]);

  const today = useMemo(() => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');

    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  }, []);

  function edit(payoutDate: ReferralPayoutDate | null) {
    setSaveError(null);
    setFieldErrors({});
    setDraft(
      payoutDate === null
        ? BLANK
        : {
            id: payoutDate.id,
            payPeriodStart: payoutDate.payPeriodStart ?? '',
            payPeriodEnd: payoutDate.payPeriodEnd ?? '',
            payoutOn: payoutDate.payoutOn,
            note: payoutDate.note ?? '',
          },
    );
  }

  async function save() {
    if (draft === null) return;

    const request: SaveReferralPayoutDateRequest = {
      // Empty means "not recorded", not "the start of time" — an empty date string is rejected by the
      // binder before validation ever sees it, so it has to go as null.
      payPeriodStart: draft.payPeriodStart || null,
      payPeriodEnd: draft.payPeriodEnd || null,
      payoutOn: draft.payoutOn,
      note: draft.note.trim() || null,
    };

    setSaving(true);
    setSaveError(null);
    setFieldErrors({});

    try {
      if (draft.id === null) {
        await api.createReferralPayoutDate(request);
      } else {
        await api.updateReferralPayoutDate(draft.id, request);
      }

      await load();
      setDraft(null);
    } catch (error: unknown) {
      if (error instanceof ApiError) {
        setFieldErrors(error.fieldErrors);
        setSaveError(Object.keys(error.fieldErrors).length > 0 ? null : error.message);
      } else {
        setSaveError(error instanceof Error ? error.message : 'It could not be saved.');
      }
    } finally {
      setSaving(false);
    }
  }

  async function remove(payoutDate: ReferralPayoutDate) {
    setSaveError(null);

    try {
      await api.deleteReferralPayoutDate(payoutDate.id);
      await load();
    } catch (error: unknown) {
      setSaveError(error instanceof Error ? error.message : 'It could not be removed.');
    }
  }

  if (loadError !== null) {
    return (
      <div className="page-message page-message--error">
        <h2>The payout dates could not be loaded</h2>
        <p>{loadError}</p>
      </div>
    );
  }

  if (payoutDates === null) {
    return <div className="page-message">Loading payout dates…</div>;
  }

  const upcoming = payoutDates.filter((d) => d.payoutOn >= today);
  const last = payoutDates.length === 0 ? null : payoutDates[payoutDates.length - 1].payoutOn;

  return (
    <div className="section-page">
      <header className="section-page__header">
        <div>
          <h1>Payout dates</h1>
          <p className="section-page__lead">
            When referral bonuses are paid. A referral's payout date is the first run on or after the
            day its 90 days are up — so a run moved here moves every referral that lands on it.
          </p>
        </div>

        <button
          type="button"
          className="button button--primary"
          onClick={() => edit(null)}
          disabled={draft?.id === null}
        >
          Add payout date
        </button>
      </header>

      {saveError !== null && <p className="dialog__error">{saveError}</p>}

      {last !== null && upcoming.length < 6 && (
        <p className="section-page__notice">
          Only {upcoming.length} run{upcoming.length === 1 ? '' : 's'} left, the last on{' '}
          {formatDate(last)}. Referrals whose 90 days fall after it will show no payout date until the
          calendar is extended.
        </p>
      )}

      <div className="table-scroll">
        <table className="table table--payout-dates">
          <thead>
            <tr>
              <th scope="col">Pay period start</th>
              <th scope="col">Pay period end</th>
              <th scope="col">Payroll date</th>
              <th scope="col">Note</th>
              <th scope="col" className="table__fit">
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {payoutDates.map((payoutDate) =>
              draft !== null && draft.id === payoutDate.id ? (
                <DraftRow
                  key={payoutDate.id}
                  draft={draft}
                  saving={saving}
                  fieldErrors={fieldErrors}
                  onChange={setDraft}
                  onSave={save}
                  onCancel={() => setDraft(null)}
                />
              ) : (
                <tr
                  key={payoutDate.id}
                  className={payoutDate.payoutOn < today ? 'table__row--done' : undefined}
                >
                  <td>{payoutDate.payPeriodStart === null ? '—' : formatDate(payoutDate.payPeriodStart)}</td>
                  <td>{payoutDate.payPeriodEnd === null ? '—' : formatDate(payoutDate.payPeriodEnd)}</td>
                  <td className="table__strong">{formatDate(payoutDate.payoutOn)}</td>
                  <td>{payoutDate.note ?? '—'}</td>
                  <td>
                    <div className="table__actions">
                      <button
                        type="button"
                        className="icon-button"
                        title="Edit this payout date"
                        aria-label={"Edit the payout date " + payoutDate.payoutOn}
                        onClick={() => edit(payoutDate)}
                        disabled={draft !== null}
                      >
                        <EditIcon />
                      </button>
                      <button
                        type="button"
                        className="icon-button icon-button--danger"
                        title="Remove this payout date"
                        aria-label={"Remove the payout date " + payoutDate.payoutOn}
                        onClick={() => remove(payoutDate)}
                        disabled={draft !== null}
                      >
                        <RemoveIcon />
                      </button>
                    </div>
                  </td>
                </tr>
              ),
            )}

            {draft !== null && draft.id === null && (
              <DraftRow
                draft={draft}
                saving={saving}
                fieldErrors={fieldErrors}
                onChange={setDraft}
                onSave={save}
                onCancel={() => setDraft(null)}
              />
            )}
          </tbody>
        </table>
      </div>

      <p className="section-page__footnote">
        Medulla's published 2026 payroll calendar, ending 22 January 2027 where the printed one ends.
        Past runs are kept — a referral paid last year still needs its date to mean something.
      </p>
    </div>
  );
}

function DraftRow(props: {
  draft: Draft;
  saving: boolean;
  fieldErrors: Record<string, string[]>;
  onChange: (draft: Draft) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const { draft, saving, fieldErrors, onChange, onSave, onCancel } = props;

  return (
    <tr className="table__row--editing">
      <td>
        <input
          className="control"
          type="date"
          value={draft.payPeriodStart}
          onChange={(e) => onChange({ ...draft, payPeriodStart: e.target.value })}
          aria-label="Pay period start"
          autoFocus
        />
        {fieldErrors.payPeriodStart?.[0] !== undefined && (
          <p className="field__error">{fieldErrors.payPeriodStart[0]}</p>
        )}
      </td>

      <td>
        <input
          className="control"
          type="date"
          value={draft.payPeriodEnd}
          onChange={(e) => onChange({ ...draft, payPeriodEnd: e.target.value })}
          aria-label="Pay period end"
        />
        {fieldErrors.payPeriodEnd?.[0] !== undefined && (
          <p className="field__error">{fieldErrors.payPeriodEnd[0]}</p>
        )}
      </td>

      <td>
        <input
          className="control"
          type="date"
          value={draft.payoutOn}
          onChange={(e) => onChange({ ...draft, payoutOn: e.target.value })}
          aria-label="Payroll date"
        />
        {fieldErrors.payoutOn?.[0] !== undefined && <p className="field__error">{fieldErrors.payoutOn[0]}</p>}
      </td>

      <td>
        <input
          className="control"
          value={draft.note}
          onChange={(e) => onChange({ ...draft, note: e.target.value })}
          placeholder="Why this one is not on the usual pattern"
        />
        {fieldErrors.note?.[0] !== undefined && <p className="field__error">{fieldErrors.note[0]}</p>}
      </td>

      <td>
        <div className="table__actions">
          <button type="button" className="button button--quiet button--small" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="button button--primary button--small" onClick={onSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </td>
    </tr>
  );
}
